import { DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { isInvestOpen } from '@app/helpers/project.helper';
import { Project, ProjectPublicResponse } from '@app/models/project.models';
import { ImgService } from '@app/services/file/img.service';
import { ProjectService } from '@app/services/projects/service';
import { AuthStore } from '@app/stores/auth.store';
import { ProjectsStore } from '@app/stores/projects/projects.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { DaysUntilPipe } from '../../../+pipes/days-until.pipe';
import { ValidatedCheckboxComponent } from '../../common/validated-checkbox/validated-checkbox.component';
import { InvestDialogComponent } from '../invest-dialog/invest-dialog.component';
import { InvestDoneDialogComponent } from '../invest-done-dialog/invest-done-dialog.component';
import { InvestInfoDialogComponent } from '../invest-info-dialog/invest-info-dialog.component';

@Component({
  selector: 'app-project-details-header',
  imports: [TranslatePipe, DaysUntilPipe, DecimalPipe, ValidatedCheckboxComponent],
  templateUrl: './project-details-header.component.html',
  styleUrls: ['./project-details-header.component.scss'],
})
export class ProjectDetailsHeaderComponent implements AfterViewInit {
  private imgService = inject(ImgService);
  private modalService = inject(NgbModal);

  readonly projectDetails = input.required<ProjectPublicResponse>();

  readonly mainImage = computed(() => {
    const images = this.projectDetails().mainImages;
    return images && images.length ? images[0] : null;
  });

  protected showFollow = computed(() => {
    return this.authStore.isLoggedIn();
  });

  protected isInvestOpen = computed(() =>
    isInvestOpen(
      this.projectDetails().state,
      this.projectDetails().startCollectionDate,
      this.projectDetails().endCollectionDate
    )
  );

  protected isLoggedIn = computed(() => this.authStore.isLoggedIn());

  readonly imgSrc = signal<SafeUrl | null>(null);

  protected projectsStore = inject(ProjectsStore);
  protected investmentProtection = signal(true);

  private authStore = inject(AuthStore);
  private projectService = inject(ProjectService);

  readonly showReadMoreLink = signal(false);
  @ViewChild('descEl') descEl?: ElementRef<HTMLElement>;

  constructor() {
    effect(() => {
      const mainImage = this.projectDetails().mainImages?.find((image) =>
        image.contentType?.includes('image'),
      );

      if (!mainImage) {
        this.imgSrc.set(null);
        return;
      }

      this.imgService.loadImage(mainImage.id).then((imgUrl) => {
        if (imgUrl !== undefined) {
          this.imgSrc.set(imgUrl);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.checkOverflow();
  }

  private checkOverflow(): void {
    const el = this.descEl?.nativeElement;
    if (!el) return;

    const text = this.projectDetails().description ?? '';
    if (!text.trim()) {
      this.showReadMoreLink.set(false);
      return;
    }

    const isOverflowing = el.scrollHeight > el.clientHeight + 1;
    this.showReadMoreLink.set(isOverflowing);
  }

  getDateString(date: Date | string): string {
    return new Date(date).toLocaleDateString();
  }

  get descriptionParagraph(): string[] {
    const text = this.projectDetails().description;
    return text
      .split(/\r?\n\r?\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  scrollToProjectDescription(e: MouseEvent) {
    e.preventDefault();

    const el = document.getElementById('project-description');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  invest(): void {
    const infoModalRef = this.modalService.open(InvestInfoDialogComponent, {
      centered: true,
    });
    infoModalRef.result.then((result) => {
      if (result === 'success') {
        this.openInvestDialog();
      }
    });
  }

  follow(projectId: string) {
    this.projectService.follow(projectId).subscribe({
      next: () => {
        const projectDetails = this.projectDetails();
        const project: Project = {
          id: projectDetails.id,
          foundingMode: projectDetails.foundingMode,
          state: projectDetails.state,
          name: projectDetails.name,
          country: projectDetails.country,
          city: projectDetails.city,
          currency: projectDetails.currency,
          projectOwner: projectDetails.projectOwner as any,
          targetAmount: projectDetails.targetAmount,
          currentAmount: projectDetails.currentAmount,
          targetColletionEndDate:
            projectDetails.targetColletionEndDate.toString(),
          interestMin: projectDetails.interestMin ?? undefined,
          interestMax: projectDetails.interestMax ?? undefined,
          yieldMin: projectDetails.yieldMin ?? undefined,
          yieldMax: projectDetails.yieldMax ?? undefined,
          loanTypePaymentStart: projectDetails.loanTypePaymentStart,
          loanTypePaymentEnd: projectDetails.loanTypePaymentEnd,
          loanTypeCapitalPaymentFrequency:
            projectDetails.loanTypeCapitalPaymentFrequency,
          loanTypeInterestPaymentFrequency:
            projectDetails.loanTypeInterestPaymentFrequency,
          classification: projectDetails.classification as any,
          mainImages: projectDetails.mainImages as any,
        };
        this.projectsStore.addFollowedProject(project);
      },
    });
  }

  unFollow(projectId: string) {
    this.projectService.unFollow(projectId).subscribe({
      next: () => {
        this.projectsStore.removeFollowedProject(projectId);
      },
    });
  }

  private openInvestDialog(): void {
    const modalRef = this.modalService.open(InvestDialogComponent, {
      centered: true,
    });
    (modalRef.componentInstance as InvestDialogComponent).projectDetails =
      this.projectDetails();
    (modalRef.componentInstance as InvestDialogComponent).investmentProtection =
      this.investmentProtection();

    modalRef.result.then((result) => {
      if (result === 'success') {
        this.openInvestDoneDialog();
      }
    });
  }

  private openInvestDoneDialog() {
    this.modalService.open(InvestDoneDialogComponent, {
      centered: true,
    });
  }
}
